<template>
  <AchievementProgressCard
    v-if="progresses.length === 1"
    :progress="progresses[0]"
    :achievement="achievementMap[progresses[0].id.split('-').at(-1)!]"
    :hide-badge
    :hide-award-indicator
  />
  <div v-else>
    <TransitionAutoHeight v-if="unachieved.length" :model-value="showAll">
      <div class="space-y-4 pb-4">
        <AchievementProgressCard
          v-for="prog in unachieved"
          :progress="prog"
          :achievement="achievementMap[prog.id.split('-').at(-1)!]"
          :hide-badge
          :hide-award-indicator
        />
      </div>
    </TransitionAutoHeight>
    <AchievementProgressCard
      :progress="heroAchievement"
      :achievement="achievementMap[heroAchievement.id.split('-').at(-1)!]"
      :hide-badge
      :hide-award-indicator
    >
      <template #afterName>
        <NamiButton
          button-type="secondary"
          @buttonClick="showAll = !showAll"
          small
          text
          pill
          :icon="showAll ? IconFold : IconArrowsMoveVertical"
        />
      </template>
    </AchievementProgressCard>
    <TransitionAutoHeight v-if="achieved.length" :model-value="showAll">
      <div class="space-y-4 pt-4">
        <AchievementProgressCard
          v-for="prog in achieved"
          :progress="prog"
          :achievement="achievementMap[prog.id.split('-')[0]]"
          :hide-badge
          :hide-award-indicator
        />
      </div>
    </TransitionAutoHeight>
  </div>
</template>

<script setup lang="ts">
import {
  type AchievementEntity,
  type AchievementProgressEntity,
  isAchievement,
} from "~/src/api";
import { IconArrowsMoveVertical, IconFold } from "@tabler/icons-vue";

const { progresses, achievements } = defineProps<{
  progresses: AchievementProgressEntity[];
  achievements?: AchievementEntity[];
  hideBadge?: boolean;
  hideAwardIndicator?: boolean;
}>();

const showAll = defineModel<boolean>({ default: false });

const achievementMap = computed(() =>
  Object.fromEntries(achievements?.map((ach) => [ach.id, ach]) ?? []),
);

const sortedProgresses = computed(() =>
  progresses.sort((a, b) => {
    const aMetric = a.relationships.find(isAchievement)?.attributes?.metric;
    const bMetric = b.relationships.find(isAchievement)?.attributes?.metric;

    if (aMetric !== bMetric) return 0;
    const progA = a.attributes.targetValue;
    const progB = b.attributes.targetValue;
    // const progA = (a.attributes.targetValue) / (a.attributes.progressValue - a.attributes.targetValue)
    // const progB = (b.attributes.targetValue) / (b.attributes.progressValue - b.attributes.targetValue)

    if (
      aMetric === "system_account_age" ||
      aMetric === "user_organization_subscription_age"
    )
      return progA - progB;
    return progB - progA;
  }),
);

const heroAchievement = computed(() => {
  for (const prog of sortedProgresses.value) {
    if (prog.attributes.targetReached) return prog;
  }

  return sortedProgresses.value?.at(-1)!;
});

const achieved = computed(() => {
  const heroIndex = sortedProgresses.value.indexOf(heroAchievement.value);
  return sortedProgresses.value.slice(heroIndex + 1);
});

const unachieved = computed(() => {
  const heroIndex = sortedProgresses.value.indexOf(heroAchievement.value);
  return sortedProgresses.value.slice(0, heroIndex);
});
</script>

<style scoped lang="postcss"></style>
